import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LikelihoodColorLevel, RiskLevel } from 'src/app/models/risk-assessment.model';
import { ConsequenceRating } from 'src/app/models/jurisdictionRiskConfig.model';
import { JurisdictionRiskConfigService } from 'src/app/services/Jurisdiction-risk-config.service';
import { parseQualitativeDimensionLabels, QualitativeDimensionLabels } from './qualitative-config.util';
import { parseLikelihoodColorLevels, parseRiskLevels } from './risk-level-parsing.util';
import { getDefaultConsequenceRatings, parseConsequenceRatings } from './consequence-ratings.util';

/**
 * The band-colour configuration a risk grid renders from: the risk-level bands (health-risk
 * colouring), the exposure-likelihood colour levels (likelihood colouring + band lookup) and the
 * qualitative dimension labels.
 */
export interface ResolvedRiskConfig {
    riskLevels: RiskLevel[];
    exposureLikelihoodLevels: LikelihoodColorLevel[];
    qualitativeDimensionLabels: QualitativeDimensionLabels;
    consequenceRatings: ConsequenceRating[];
}

/** Minimal shape of the tenant risk-config blob the resolution chain reads. */
export interface TenantRiskConfigSource {
    riskLevels?: string;
    exposureLikelihoodLevels?: string;
}

/**
 * DASH-2718 (item 2): the single, shared band-colour resolution chain used by BOTH the Risk
 * Register (`RiskRegisterComponent`) and the Sampling Plan detail matrix
 * (`SamplePlanningDetailComponent`) so the two grids resolve identical bands/colours and can no
 * longer drift apart.
 *
 * Precedence mirrors the RA register exactly:
 *   1. tenant `riskLevels` / `exposureLikelihoodLevels` as the base, then
 *   2. if the assessment/plan has a `jurisdictionId`, the jurisdiction risk config OVERRIDES each
 *      of those two collections when it supplies a non-empty set, and
 *   3. `qualitativeConfig` supplies the qualitative dimension labels.
 *
 * A missing/failed jurisdiction config degrades to the tenant base (via `catchError`) rather than
 * blanking the grid. The Sampling Plan matrix reads its `jurisdictionId` from
 * `SamplingPlanMatrix.referenceRiskAssessmentJurisdictionId` (populated by the paired BE ticket),
 * so no second round-trip to the RA is needed.
 */
export function resolveRiskConfig(
    tenant: TenantRiskConfigSource | null | undefined,
    jurisdictionId: string | null | undefined,
    jurisdictionRiskConfigService: JurisdictionRiskConfigService,
    options: { defaultColor: string; defaultLabels?: QualitativeDimensionLabels },
): Observable<ResolvedRiskConfig> {
    const defaultLabels = options.defaultLabels ?? { exceedanceLabel: '', frequencyLabel: '' };
    const tenantRiskLevels = parseRiskLevels(tenant?.riskLevels);
    const tenantLikelihoodLevels = parseLikelihoodColorLevels(
        tenant?.exposureLikelihoodLevels,
        options.defaultColor,
    );

    const tenantResolved: ResolvedRiskConfig = {
        riskLevels: tenantRiskLevels,
        exposureLikelihoodLevels: tenantLikelihoodLevels,
        qualitativeDimensionLabels: parseQualitativeDimensionLabels(undefined, defaultLabels),
        // Consequence ratings are configured per jurisdiction; without one, fall back to the
        // default scale (mirrors the single-entry details popup precedence exactly).
        consequenceRatings: getDefaultConsequenceRatings(),
    };

    if (!jurisdictionId) {
        return of(tenantResolved);
    }

    return jurisdictionRiskConfigService.getByJurisdictionId(jurisdictionId).pipe(
        map((config) => {
            // config can be null when the jurisdiction has no risk config.
            const jurisdictionRiskLevels = config ? parseRiskLevels(config.riskLevels) : [];
            const jurisdictionLikelihoodLevels = config
                ? parseLikelihoodColorLevels(config.exposureLikelihoodLevels, options.defaultColor)
                : [];

            return {
                riskLevels: jurisdictionRiskLevels.length > 0 ? jurisdictionRiskLevels : tenantRiskLevels,
                exposureLikelihoodLevels:
                    jurisdictionLikelihoodLevels.length > 0
                        ? jurisdictionLikelihoodLevels
                        : tenantLikelihoodLevels,
                qualitativeDimensionLabels: parseQualitativeDimensionLabels(
                    config?.qualitativeConfig,
                    defaultLabels,
                ),
                consequenceRatings: parseConsequenceRatings(config?.exposureLikelihoodLevels),
            };
        }),
        // A missing jurisdiction config must degrade to the tenant base, not blank the grid.
        catchError(() => of(tenantResolved)),
    );
}
